import React, { useCallback, useEffect, useRef, useState } from 'react';

type SignaturePadProps = {
  value?: string | null;
  onChange: (dataUrl: string | null) => void;
  height?: number;
};

/**
 * Touch/mouse signature pad. Produces a PNG data URL with transparent background and dark strokes
 * whenever the user lifts their pointer. Clearing produces null.
 */
const SignaturePad: React.FC<SignaturePadProps> = ({ value, onChange, height = 180 }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const drawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const [hasStrokes, setHasStrokes] = useState(!!value);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const dpr = window.devicePixelRatio || 1;
    const width = container.clientWidth;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = '#0f172a';
      ctx.clearRect(0, 0, width, height);
    }
  }, [height]);

  useEffect(() => {
    resize();
    const onWin = () => resize();
    window.addEventListener('resize', onWin);
    return () => window.removeEventListener('resize', onWin);
  }, [resize]);

  useEffect(() => {
    if (!value?.startsWith('data:image')) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const width = container.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    const img = new Image();
    img.onload = () => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      ctx.restore();
      setHasStrokes(true);
    };
    img.src = value;
  }, [value]);

  const pointerPos = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drawing.current = true;
    lastPoint.current = pointerPos(e);
  };

  const onMove = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !lastPoint.current) return;
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
    setHasStrokes(true);
  };

  const onUp = () => {
    drawing.current = false;
    lastPoint.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      onChange(canvas.toDataURL('image/png'));
    } catch {
      // ignore
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setHasStrokes(false);
    onChange(null);
  };

  return (
    <div>
      <div
        ref={containerRef}
        className="w-full border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white touch-none"
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          className="block w-full select-none"
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-slate-500">
          {hasStrokes ? 'Sign above' : 'Draw signature'}
        </div>
        <button
          type="button"
          onClick={clear}
          className="text-xs font-semibold text-slate-600 hover:text-slate-900"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
